import {
  type ChannelLimitData,
  type EventInterval,
  type TherapySettingsResult,
  FeatureSidebarSeparator,
  TherapyDayData,
  TherapyTimeInterval,
  SignalViewer,
  SignalViewerRef,
} from '@lmt/signal-viewer-ui';
import React, { useEffect, useRef, useState } from 'react';
import { DragAndDrop } from './DragAndDrop';
import {
  type EdfFileError,
  type ParsedEdfInfo,
  type TherapyGeneration,
} from '@lmt/signal-viewer-processor';
import { normalizeUpload } from '../utils/normalize-upload';
import './InteractiveChartsView.css';
import {
  INTERVAL_MS,
  PredefinedZoomInterval,
} from '../components/ZoomIntervalSelector';
import { useReadQueryParamsFromLocation } from '../utils/query-helper';
import { useMutation } from '@tanstack/react-query';
import { findDemoFileIndex } from '../utils/demo-files';
import { postRenderReport } from '../api/render-report';
import { InteractiveChartsViewHeader } from './InteractiveChartsViewHeader';
import { GeneratePdfButton } from '../components/GeneratePdfButton';
import { DownloadJpegButton } from '../components/DownloadJpegButton';
import { SeparateWindowButton } from '../components/SeparateWindowButton';
import { SplitScreenButton } from '../components/SplitScreenToggleButton';
import { reorderChannels, type DropPosition } from '@lmt/signal-viewer-ui';
import { FileUploadData } from '../utils/file-upload';
import {
  ChannelsProfileProvider,
  useChannelsProfile,
} from '../contexts/ChannelsProfileContext';
import { FileSessionProvider } from '../contexts/FileSessionContext';
import { DayNavigationProvider } from '../contexts/DayNavigationContext';
import {
  backendEventsToIntervals,
  getGlobalEventsForGeneration,
  getTherapyArchiveData,
} from '../utils/events-helper';
import { useNotifications } from '../components/notifications/NotificationProvider';
import {
  isFileTooLargeApiError,
  isTherapySettingsUnavailable,
  showEventsErrorNotification,
  showEventsUnavailableNotification,
  showTherapyArchiveErrorNotification,
  showTherapySettingsErrorNotification,
  showTherapySettingsUnavailableNotification,
} from './upload-notifications';
import { useUploadMutation } from './use-upload-mutation';

type UploadSetupResult = {
  uploadedData: FileUploadData;
  therapyData: TherapyDayData;
  profileSwitchId: string;
  headerGeneration: TherapyGeneration | null;
  therapySettings: TherapySettingsResult | null;
  limits: ChannelLimitData[];
  therapySettingsUnavailable: boolean;
  therapySettingsError: string | null;
  eventsUnavailable: boolean;
  eventsError: string | null;
  archiveError: unknown | null;
};

const InteractiveChartsViewInner: React.FC = () => {
  const {
    currentProfile,
    hiddenChannelIds,
    switchProfile: onProfileSwitch,
    saveProfile: onProfileSave,
    setChannelOrder,
    setChannelHidden,
  } = useChannelsProfile();
  const chartRef = useRef<SignalViewerRef | null>(null);
  const [therapyDateTimeInterval, setTherapyDateTimeInterval] = useState(
    TherapyTimeInterval.TherapyDay,
  );
  const [isSplitScreenMode, setIsSplitScreenMode] = useState<boolean>();
  const [isBusy, setIsBusy] = useState(false);
  const [availableSignalTypes, setAvailableSignalTypes] = useState<string[]>(
    [],
  );
  const [selectedZoomInterval, setSelectedZoomInterval] =
    useState<PredefinedZoomInterval | null>('1d');
  const [uploadedParsedEdfInfo, setUploadedParsedEdfInfo] = useState<
    ParsedEdfInfo[]
  >([]);
  const [selectedDayMs, setSelectedDayMs] = useState<number | null>(null);
  const [uploadedFileHeader, setUploadedFileHeader] = useState<{
    fileNames: string[];
    generation: TherapyGeneration | null;
  } | null>(null);
  const [uploadedTherapyData, setUploadedTherapyData] = useState<
    TherapyDayData | undefined
  >(undefined);
  const { timeIntervalFromQuery } = useReadQueryParamsFromLocation();
  const notifications = useNotifications();

  useEffect(() => {
    if (timeIntervalFromQuery) {
      setTherapyDateTimeInterval(timeIntervalFromQuery);
    }
  }, [timeIntervalFromQuery]);

  const handleReorderChannels = (
    draggedChannelId: string,
    targetChannelId: string,
    position?: DropPosition,
  ) => {
    const nextConfig = reorderChannels(
      currentProfile.config,
      draggedChannelId,
      targetChannelId,
      position,
    );

    setChannelOrder(nextConfig.map((channel) => channel.id));
  };

  const handleTimeIntervalSwitchClick = () => {
    setTherapyDateTimeInterval((prev) =>
      prev === TherapyTimeInterval.TherapyDay
        ? TherapyTimeInterval.CalendarDay
        : TherapyTimeInterval.TherapyDay,
    );
  };

  // The backend renders reports headlessly, so only demo files (addressed by their
  // DEMO_FILES index) can be reported; user uploads fall back to the first demo.
  const reportMutation = useMutation({
    mutationFn: async () =>
      await postRenderReport(
        Math.max(findDemoFileIndex(uploadedFileHeader?.fileNames), 0),
        therapyDateTimeInterval,
      ),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    onError: () => {
      alert('Error occurred during report generation');
    },
  });

  const {
    uploadFiles,
    handleParseProgress,
    completeUploadNotification,
    handleParseErrors,
  } = useUploadMutation<UploadSetupResult>({
    mutationFn: async (uploadedData, reportProgress) => {
      reportProgress(5);
      const { therapyData, edfManifest, profileSwitchId, headerGeneration } =
        await normalizeUpload(uploadedData);
      reportProgress(15);

      let therapySettings: TherapySettingsResult | null = null;
      let limits: ChannelLimitData[] = [];
      let therapySettingsUnavailable = false;
      let therapySettingsError: string | null = null;
      let backendEvents: EventInterval[] | null = null;
      let eventsUnavailable = false;
      let eventsError: string | null = null;
      let archiveError: unknown | null = null;

      try {
        // Null archive data is a custom upload with no archive to read —
        // both unavailable flags stay false.
        const archiveData = await getTherapyArchiveData(
          uploadedData,
          edfManifest,
        );
        if (archiveData) {
          therapySettings = archiveData.therapySettings;
          limits = archiveData.limits;
          therapySettingsUnavailable =
            isTherapySettingsUnavailable(therapySettings);
          therapySettingsError = archiveData.therapySettingsError;
          eventsUnavailable = archiveData.events === null;
          eventsError = archiveData.eventsError;
          backendEvents = archiveData.events
            ? backendEventsToIntervals(archiveData.events)
            : null;
        }
      } catch (error) {
        if (isFileTooLargeApiError(error)) {
          throw error;
        }

        archiveError = error;
      }
      reportProgress(20);

      return {
        uploadedData,
        therapyData: {
          ...therapyData,
          events: backendEvents ?? therapyData.events,
        },
        profileSwitchId,
        headerGeneration,
        therapySettings,
        limits,
        therapySettingsUnavailable,
        therapySettingsError,
        eventsUnavailable,
        eventsError,
        archiveError,
      };
    },
    onUploadStart: () => {
      setIsSplitScreenMode(false);
      setIsBusy(true);
      setUploadedParsedEdfInfo([]);
    },
    onUploadSuccess: (result) => {
      if (result.archiveError) {
        showTherapyArchiveErrorNotification(notifications, result.archiveError);
      }

      if (result.therapySettingsError) {
        showTherapySettingsErrorNotification(
          notifications,
          result.therapySettingsError,
        );
      } else if (result.therapySettingsUnavailable) {
        showTherapySettingsUnavailableNotification(notifications);
      }

      if (result.eventsError) {
        showEventsErrorNotification(notifications, result.eventsError);
      } else if (result.eventsUnavailable) {
        showEventsUnavailableNotification(notifications);
      }

      setUploadedFileHeader({
        fileNames: [result.uploadedData.sourceFileName],
        generation: result.headerGeneration,
      });
      onProfileSwitch(result.profileSwitchId);
      setUploadedTherapyData({
        ...result.therapyData,
        therapySettings: result.therapySettings,
        limits: result.limits,
      });
    },
    onUploadError: () => {
      setIsBusy(false);
    },
  });

  const therapyData = uploadedTherapyData;
  const parsedEdfInfo = uploadedParsedEdfInfo;
  const isUploadMode = uploadedTherapyData !== undefined;

  const onSuccess = (parsedInfo: ParsedEdfInfo[]) => {
    setUploadedParsedEdfInfo(parsedInfo);

    setAvailableSignalTypes(
      parsedInfo.flatMap((info) => info.availableSignalTypes),
    );

    if (isBusy) {
      setIsBusy(false);
    }

    completeUploadNotification(parsedInfo);
  };

  const handleUpload = async (uploadedData: FileUploadData) => {
    await uploadFiles(uploadedData);
  };

  const handleViewerParseError = (errors: EdfFileError[]) => {
    setIsBusy(false);
    handleParseErrors(errors);
  };

  const handleEventTypeVisualsApplied = (eventName: string) => {
    notifications.showSuccess({
      title: 'Your changes were applied',
      description: `Updated all ${eventName} events`,
    });
  };

  // No busy state here: nothing re-parses after the view empties, so it would never clear.
  const handleCancelUpload = () => {
    setUploadedFileHeader(null);
    setUploadedTherapyData(undefined);
    setUploadedParsedEdfInfo([]);
    setAvailableSignalTypes([]);
  };

  const handleRemoveChannelById = (channelId: string) => {
    onProfileSave({
      ...currentProfile,
      config: currentProfile.config.filter((x) => x.id !== channelId),
    });
  };

  const handleSplitScreenClick = () => {
    setIsSplitScreenMode((prev) => !prev);
  };

  const handleOnZoomChange = (intervalMs: number) => {
    const driftToleranceMs = 1000;
    const matchedPreset = Object.entries(INTERVAL_MS).find(
      ([, presetMs]) => Math.abs(presetMs - intervalMs) <= driftToleranceMs,
    );

    setSelectedZoomInterval(
      matchedPreset ? (matchedPreset[0] as PredefinedZoomInterval) : null,
    );
  };

  const fileSessionValue = {
    isUploadMode,
    currentFileNames: uploadedFileHeader?.fileNames,
    headerGeneration: uploadedFileHeader?.generation ?? null,
    parsedEdfInfo,
    availableSignalTypes,
    dataGeneration: therapyData?.generation ?? null,
    onCancelUpload: handleCancelUpload,
    onUploadFiles: handleUpload,
  };

  const dayNavigationValue = {
    selectedDayMs,
    onSelectedDayChange: setSelectedDayMs,
    therapyTimeInterval: therapyDateTimeInterval,
    onTimeIntervalToggle: handleTimeIntervalSwitchClick,
  };

  return (
    <FileSessionProvider value={fileSessionValue}>
      <DayNavigationProvider value={dayNavigationValue}>
        <InteractiveChartsViewHeader
          ref={chartRef}
          selectedZoomInterval={selectedZoomInterval}
          onZoomIntervalChange={setSelectedZoomInterval}
        />

        <div className='interactive-charts'>
          <div className='interactive-charts__body'>
            <SignalViewer
              ref={chartRef}
              isLoading={isBusy}
              containerClassName='signal-viewer__charts'
              providedConfigs={currentProfile.config}
              globalEvents={getGlobalEventsForGeneration(
                therapyData?.generation,
              )}
              hiddenChannelIds={hiddenChannelIds}
              onChannelVisibilityToggle={setChannelHidden}
              data={therapyData}
              selectedDayMs={selectedDayMs}
              enableResize
              onSuccess={onSuccess}
              onParseProgress={handleParseProgress}
              onParseError={handleViewerParseError}
              onEventTypeVisualsApplied={handleEventTypeVisualsApplied}
              therapyTimeInterval={therapyDateTimeInterval}
              enableSplitScreen={isSplitScreenMode}
              onZoomIntervalChange={handleOnZoomChange}
              onChannelDelete={handleRemoveChannelById}
              onChannelsReorder={handleReorderChannels}
              toolbarExtension={
                <>
                  <SplitScreenButton
                    checked={isSplitScreenMode ?? false}
                    onChange={handleSplitScreenClick}
                  />
                  <SeparateWindowButton />
                  <FeatureSidebarSeparator />
                  <GeneratePdfButton
                    onGenerateReport={() => reportMutation.mutate()}
                    isGenerating={reportMutation.isPending}
                  />
                  <DownloadJpegButton />
                </>
              }
            />
          </div>

          <DragAndDrop onUpload={handleUpload} />
        </div>
      </DayNavigationProvider>
    </FileSessionProvider>
  );
};

export const InteractiveChartsView: React.FC = () => (
  <ChannelsProfileProvider>
    <InteractiveChartsViewInner />
  </ChannelsProfileProvider>
);
